"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.escapeBelowSpotlight = escapeBelowSpotlight;
exports.findLive = findLive;
exports.Spotlight = Spotlight;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const overlay_1 = require("../lib/overlay");
const button_1 = require("./button");
const icon_1 = require("./icon");
/** Marks an Escape the tour sends to a surface it opened, so the spotlight lets it pass. */
const PASS = Symbol.for('forecastify.spotlight.pass');
/**
 * Escape, for the topmost surface BELOW the spotlight — the task, the form the tour opened.
 *
 * ⚠️⚠️ **THE SPOTLIGHT IS A MODAL SURFACE TOO, AND IT NEVER ANSWERS THIS ONE.** A plain synthetic
 * Escape reached whichever surface `useModalSurface`'s stack had on top, and when nothing the tour
 * opened was still there — a popover was, which is not a modal surface — that was the spotlight, and
 * the tour closed itself. A person's own Escape still closes the tour.
 */
function escapeBelowSpotlight() {
    const event = new KeyboardEvent('keydown', { key: 'Escape', bubbles: true, cancelable: true });
    Object.defineProperty(event, PASS, { value: true });
    document.dispatchEvent(event);
}
/**
 * The first element matching `selector` that is neither part of the spotlight nor on its way out.
 *
 * ⚠️ **The card is itself a `role="dialog"`**, and so is a dialog playing its exit (`inert` while it
 * does) — a step pointing at "the open dialog" must find neither.
 */
function findLive(selector) {
    for (const candidate of document.querySelectorAll(selector)) {
        if (candidate.closest('[data-spotlight]') === null && candidate.closest('[inert]') === null) {
            return candidate;
        }
    }
    return null;
}
const CARD_WIDTH = 320;
/** Breathing room between the target's edge and the cut-out's. */
const PAD = 6;
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ F-7 · one step of a guided tour: a cut-out around something, and a card.   │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Domain-agnostic: it is handed a selector and translated words, and knows nothing about which
 * screens exist. The tour that drives it — which steps, in what order, on which route, for whom — is
 * the app's (`decisions.md` F-2).
 *
 * ⚠️⚠️ **A FORCED TOUR BLOCKS EVERYTHING, THE HIGHLIGHTED ELEMENT INCLUDED** (F-2, the product owner's
 * ruling). A full-window layer above every other surface takes every pointer event; the cut-out is
 * drawn with `pointer-events: none` over that layer, so it shows the target without making it
 * pressable. The card's three controls are the only things that act.
 *
 * ⚠️ **The cut-out is one element and a shadow**: a box at the target's rectangle whose `box-shadow`
 * is the scrim spreading 9999px outwards. One layer, no SVG mask. Each step gets a fresh box that
 * snaps in from its own centre with a short scale — transform only, so nothing reflows.
 *
 * ⚠️ **Focus is trapped in the card and Escape closes**, through `useModalSurface`, like every modal
 * here. ← and → move between steps while the focus is in the card.
 *
 * ⚠️ **It waits for the target**, because the tour navigates and the next screen mounts after the
 * step does. A target that never arrives is reported, never waited on forever — a tour that stalls on
 * a missing element is a page somebody cannot use.
 */
function Spotlight({ open, target, title, body, side = 'bottom', index, total, labels, onNext, onPrevious, onClose, onTargetMissing, waitMs = 3000, secondary, position, onSkipSection, busy = false, }) {
    const titleId = `${(0, react_1.useId)()}-title`;
    /* Whether the Escape being handled right now is the tour's own (`escapeBelowSpotlight`). Read in
       the window's capture phase, which runs before the modal surface's listener on `document`. */
    const passing = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        const mark = (event) => {
            passing.current = event[PASS] === true;
        };
        window.addEventListener('keydown', mark, true);
        return () => {
            window.removeEventListener('keydown', mark, true);
        };
    }, [open]);
    const { ref } = (0, overlay_1.useModalSurface)(open, () => {
        if (!passing.current)
            onClose();
    });
    const found = useTarget(open && !busy, busy ? null : target, waitMs);
    /* Reported from an effect, once per miss, so the caller's state change cannot loop a render. */
    const missing = (0, react_1.useRef)(onTargetMissing);
    missing.current = onTargetMissing;
    (0, react_1.useEffect)(() => {
        if (found.state === 'missing')
            missing.current?.();
    }, [found.state]);
    const rect = found.state === 'found' ? found.rect : null;
    /**
     * Where the card sits. ⚠️ **While the tour works, or the target is still being looked for, the
     * card stays where the last target put it** rather than jumping to the middle and back — it moves
     * once, when the new target lands.
     */
    const placed = (0, react_1.useRef)(null);
    const placement = rect !== null
        ? covers(rect)
            ? /* A target that is most of the window — a whole screen, a grid — leaves no "beside" to
                 put the card in, so it docks in the corner over the target. */
                {
                    position: 'fixed',
                    right: 24,
                    bottom: 24,
                    width: CARD_WIDTH,
                    maxWidth: 'calc(100vw - 32px)',
                    zIndex: 81,
                }
            : {
                ...(0, overlay_1.anchorTo)(rect, { side, width: CARD_WIDTH, offset: PAD + 10 }),
                maxWidth: 'calc(100vw - 16px)',
                zIndex: 81,
            }
        : target !== null && placed.current !== null
            ? placed.current
            : {
                position: 'fixed',
                top: '50%',
                left: '50%',
                width: CARD_WIDTH,
                maxWidth: 'calc(100vw - 32px)',
                transform: 'translate(-50%, -50%)',
            };
    if (rect !== null)
        placed.current = placement;
    /**
     * The snap, replayed whenever the cut-out lands on a new target: drop the class, force a reflow,
     * add it back. A re-render cannot restart a CSS animation on an element that stays mounted.
     */
    const hole = (0, react_1.useRef)(null);
    const landed = rect === null ? null : index;
    (0, react_1.useEffect)(() => {
        const node = hole.current;
        if (node === null || landed === null)
            return;
        node.classList.remove('spot-in');
        void node.offsetWidth;
        node.classList.add('spot-in');
    }, [landed]);
    /* ⚠️ The card mounts after its target is found, and again on every step (`key`), so the modal
       surface's open-time focus has usually already happened onto nothing. The primary action takes
       focus each time, which is also what makes Enter mean "next". */
    (0, react_1.useEffect)(() => {
        if (!open)
            return;
        ref.current?.querySelector('[data-spotlight-primary]')?.focus();
    }, [open, index, ref]);
    if (!open)
        return null;
    const last = index === total - 1;
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { className: "fixed inset-0 z-[80]", "data-spotlight": "1", children: [(0, jsx_runtime_1.jsx)("div", { ref: hole, "aria-hidden": true, className: rect === null
                    ? 'pointer-events-none fixed inset-0 bg-[color:var(--scrim)]'
                    : 'ring-primary pointer-events-none fixed rounded-lg ring-2', style: rect === null
                    ? undefined
                    : {
                        top: rect.top - PAD,
                        left: rect.left - PAD,
                        width: rect.width + PAD * 2,
                        height: rect.height + PAD * 2,
                        boxShadow: '0 0 0 9999px var(--scrim)',
                    } }), (0, jsx_runtime_1.jsxs)("div", { ref: ref, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "aria-busy": busy || undefined, tabIndex: -1, onKeyDown: (event) => {
                    if (event.key === 'ArrowRight') {
                        event.preventDefault();
                        onNext();
                    }
                    else if (event.key === 'ArrowLeft' && index > 0) {
                        event.preventDefault();
                        onPrevious();
                    }
                }, className: "pop-in bg-popover text-popover-foreground shadow-pop flex flex-col gap-2 rounded-lg border p-4 outline-none", style: placement, children: [(0, jsx_runtime_1.jsxs)("div", { className: "flex items-center gap-2", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11.5px] tabular-nums", children: position ?? labels.step(index + 1, total) }), busy ? ((0, jsx_runtime_1.jsx)("span", { role: "status", "aria-label": labels.busy, className: "text-muted-foreground flex", children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "refresh-cw", size: 12, className: "animate-spin" }) })) : null, (0, jsx_runtime_1.jsx)("div", { className: "flex-1" }), (0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "icon-sm", onClick: onClose, "aria-label": labels.close, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: "x", size: 14 }) })] }), (0, jsx_runtime_1.jsx)("div", { id: titleId, className: "text-[14px] font-semibold", children: title }), (0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground text-[13px] leading-relaxed", children: body }), (0, jsx_runtime_1.jsxs)("div", { className: "mt-1 flex items-center justify-end gap-2", children: [onSkipSection === undefined || labels.skipSection === undefined ? null : ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: "link", size: "sm", className: "mr-auto h-auto px-0", onClick: onSkipSection, children: labels.skipSection })), index === 0 ? null : ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", size: "sm", onClick: onPrevious, children: labels.previous })), last && secondary !== undefined ? ((0, jsx_runtime_1.jsx)(button_1.Button, { variant: "outline", size: "sm", onClick: secondary.onSelect, children: secondary.label })) : null, (0, jsx_runtime_1.jsx)(button_1.Button, { "data-spotlight-primary": true, size: "sm", onClick: onNext, children: last ? labels.finish : labels.next })] })] }, index)] }), document.body);
}
/** Whether a target takes up so much of the window that nothing can sit beside it. */
function covers(rect) {
    return rect.height > window.innerHeight * 0.55 || rect.width > window.innerWidth * 0.7;
}
/**
 * Where the target is, kept current while the step is open.
 *
 * ⚠️ **Polled, then measured on every scroll and resize.** The element may not exist yet — the
 * tour has just navigated — so it is looked for every 100ms until `waitMs`; once found it is
 * scrolled into view and re-measured whenever anything moves it, scrolls inside the shell's own
 * scroll regions included (hence the capture).
 *
 * ⚠️ **No size counts as missing.** A sidebar row below 900px is in the DOM and `display: none`;
 * cutting a hole around a zero-size box at (0, 0) would highlight the corner of the window.
 */
function useTarget(open, selector, waitMs) {
    /* ⚠️ Each answer remembers which selector it is about. Without that, the render that brings in a
       new step still holds the previous step's answer — and a centred card flashes for one frame
       before the hunt for the new target begins. */
    const [found, setFound] = (0, react_1.useState)({
        for: null,
        target: { state: 'none' },
    });
    (0, react_1.useEffect)(() => {
        if (!open || selector === null) {
            setFound({ for: selector, target: { state: 'none' } });
            return;
        }
        setFound({ for: selector, target: { state: 'waiting' } });
        let element = null;
        let last = '';
        const started = Date.now();
        const measure = () => {
            if (element === null)
                return;
            /* ⚠️ The page changed under it: the element found a moment ago was the previous screen's,
               and a detached node measures 0×0 — which would read as "missing". Look again instead. */
            if (!element.isConnected || element.closest('[inert]') !== null) {
                element = findLive(selector);
                if (element === null)
                    return;
            }
            const rect = element.getBoundingClientRect();
            if (rect.width === 0 && rect.height === 0) {
                setFound({ for: selector, target: { state: 'missing' } });
                return;
            }
            /* Only a real move re-renders: the follow-up below measures five times a second. */
            const shape = `${String(rect.top)},${String(rect.left)},${String(rect.width)},${String(rect.height)}`;
            if (shape === last)
                return;
            last = shape;
            setFound({ for: selector, target: { state: 'found', rect } });
        };
        /**
         * ⚠️⚠️ **KEPT MEASURING, BECAUSE THE FIRST MEASUREMENT IS OFTEN WRONG.** A table is found while
         * its rows are still arriving, a card mid-animation, a screen before its fonts settle — and a
         * frame measured once stays the size of that first moment. Re-measured five times a second
         * while the step is open; it costs one layout read and re-renders only when something moved.
         */
        const follow = window.setInterval(measure, 200);
        const poll = window.setInterval(() => {
            const candidate = findLive(selector);
            if (candidate !== null) {
                element = candidate;
                window.clearInterval(poll);
                /* ⚠️ `scrollIntoView` is absent in jsdom, which has no layout to scroll. */
                const scroll = candidate
                    .scrollIntoView;
                scroll?.call(candidate, { block: 'center', inline: 'nearest' });
                measure();
                return;
            }
            if (Date.now() - started >= waitMs) {
                window.clearInterval(poll);
                setFound({ for: selector, target: { state: 'missing' } });
            }
        }, 100);
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.clearInterval(poll);
            window.clearInterval(follow);
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [open, selector, waitMs]);
    if (found.for !== selector)
        return selector === null ? { state: 'none' } : { state: 'waiting' };
    return found.target;
}
//# sourceMappingURL=spotlight.js.map